import type { Vec3 } from '../types';

export type FlightView = 'behind' | 'side' | 'top';
/** Unobscured canvas area in normalized device coordinates. */
export interface FrameWindow { left: number; right: number; bottom: number; top: number }
const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const unit = (a: Vec3) => scale(a, 1 / Math.hypot(a.x, a.y, a.z));

/** Perspective fit including depth and UI occlusion. All distances are meters. */
export function fitFlight(points: readonly Vec3[], view: FlightView, aspect: number,
  window: FrameWindow = { left: -.88, right: .88, bottom: -.82, top: .82 },
) {
  const valid = points.filter(p => Number.isFinite(p.x + p.y + p.z));
  if (!valid.length) return null;
  const min = { x: Infinity, y: Infinity, z: Infinity };
  const max = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const p of valid) for (const axis of ['x', 'y', 'z'] as const) {
    min[axis] = Math.min(min[axis], p[axis]); max[axis] = Math.max(max[axis], p[axis]);
  }
  const center = scale(add(min, max), .5);
  const offset = view === 'side' ? { x: -1, y: .25, z: -.10 }
    : view === 'top' ? { x: 0, y: 1, z: -.025 } : { x: .34, y: .48, z: -1 };
  const forward = unit(scale(offset, -1));
  const right = unit(cross({ x: 0, y: 1, z: 0 }, forward));
  const up = cross(forward, right);
  const fov = .8;
  const tanY = Math.tan(fov / 2), tanX = tanY * Math.max(.1, aspect);
  const projected = valid.map(p => {
    const v = add(p, scale(center, -1));
    return { x: dot(v, right), y: dot(v, up), z: dot(v, forward) };
  });
  const limits = (distance: number) => {
    let lx = -Infinity, ux = Infinity, ly = -Infinity, uy = Infinity;
    for (const p of projected) {
      const depth = distance + p.z;
      if (depth < 2) return null;
      lx = Math.max(lx, p.x - window.right * tanX * depth);
      ux = Math.min(ux, p.x - window.left * tanX * depth);
      ly = Math.max(ly, p.y - window.top * tanY * depth);
      uy = Math.min(uy, p.y - window.bottom * tanY * depth);
    }
    return lx <= ux && ly <= uy ? { x: (lx + ux) / 2, y: (ly + uy) / 2 } : null;
  };
  let low = 8, high = 8192;
  for (let i = 0; i < 40; i++) {
    const mid = (low + high) / 2;
    if (limits(mid)) high = mid; else low = mid;
  }
  const distance = high * 1.08;
  const shift = limits(distance)!;
  const target = add(center, add(scale(right, shift.x), scale(up, shift.y)));
  return { position: add(target, scale(forward, -distance)), target, fov, forward, right, up, distance };
}
